/**
 * Detalhe de município PE — Brasil Transparente.
 *
 * Rota: /brasil-transparente/municipio/{nome}
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, AlertCircle, Share2, ExternalLink, Building2, ChevronRight, Star,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const YEAR_ELEITORAL = 2026;

const brl = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';
const num = (v) => (typeof v === 'number' ? v.toLocaleString('pt-BR') : '—');

export default function BrasilTransparenteMunicipio() {
  const { nome } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const nomeDec = decodeURIComponent(nome || '');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr(false);
      try {
        const r = await axios.get(
          `${API}/public/brasil-transparente/PE/municipio/${encodeURIComponent(nomeDec)}`,
          { timeout: 30000 },
        );
        if (!cancel) setData(r.data);
      } catch { if (!cancel) setErr(true); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [nomeDec]);

  const shareUrl = `${BACKEND_URL}/api/public/brasil-transparente/PE/share/municipio/${encodeURIComponent(nomeDec)}.png`;

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24" data-testid="bt-municipio-page">
      <header className="border-b border-neutral-800 bg-gradient-to-br from-emerald-900/40 via-neutral-950 to-neutral-950 px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/brasil-transparente"
            className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-emerald-400"
            data-testid="back-to-bt"
          >
            <ArrowLeft size={14} /> Brasil Transparente
          </Link>
          <div className="mt-3 flex items-center gap-2 mb-1">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-mono">
              Município · PE
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-none">
            {nomeDec}
          </h1>

          {!loading && data?.ok && (
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="share-muni-png"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/20"
            >
              <Share2 size={14} /> Card para WhatsApp
            </a>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {loading && <Skeleton />}
        {!loading && err && <ErrorBox />}
        {!loading && data && data.ok && data.empty && (
          <Card className="bg-neutral-900 border-neutral-800 p-6 text-sm text-neutral-400">
            Nenhuma emenda registrada para este município no CSV atual.
          </Card>
        )}

        {!loading && data && data.ok && !data.empty && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Metric label="Total recebido" value={brl(data.total)} testid="kpi-total" />
              <Metric label="Pagamentos" value={num(data.count)} testid="kpi-count" />
              <Metric
                label="Anos com registro"
                value={num((data.por_ano || []).length)}
                testid="kpi-anos"
              />
              <Metric
                label="Beneficiários únicos"
                value={num(data.beneficiarios_total || (data.top_favorecidos || []).length)}
                testid="kpi-benef"
              />
            </div>

            {/* Áreas (heurística) */}
            <Section
              title="Onde foi gasto (por área)"
              subtitle="Classificação automática baseada no nome do beneficiário. Não é oficial."
              testid="section-areas"
            >
              <div className="space-y-1.5">
                {(data.por_area || []).map((a, i) => (
                  <Bar
                    key={a.area_key + i}
                    label={a.label}
                    value={a.total}
                    max={data.total}
                    hint={`${num(a.count)} pagamentos`}
                    testid={`area-bar-${a.area_key}`}
                  />
                ))}
              </div>
            </Section>

            {/* Por ano */}
            <Section title="Por ano" testid="section-anos">
              <div className="space-y-1.5">
                {(data.por_ano || []).map((y) => {
                  const isEleitoral = y.ano === YEAR_ELEITORAL;
                  return (
                    <div
                      key={y.ano}
                      className={`relative rounded overflow-hidden border ${
                        isEleitoral
                          ? 'bg-amber-500/5 border-amber-500/30'
                          : 'bg-neutral-900 border-neutral-800'
                      }`}
                      data-testid={`ano-bar-${y.ano}`}
                    >
                      <div
                        className={`absolute inset-y-0 left-0 ${
                          isEleitoral ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                        }`}
                        style={{
                          width: `${Math.max(3, (y.total / Math.max(...(data.por_ano || []).map((x) => x.total))) * 100)}%`,
                        }}
                      />
                      <div className="relative flex items-center gap-2 px-3 py-2">
                        <div className="min-w-0 flex-1 flex items-center gap-2">
                          <span className="text-sm text-white">{y.ano}</span>
                          {isEleitoral && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-mono uppercase"
                              data-testid={`badge-eleitoral-${y.ano}`}
                            >
                              <Star size={9} /> Eleitoral
                            </span>
                          )}
                          <span className="text-[10px] text-neutral-500">
                            {num(y.count)} pagamentos
                          </span>
                        </div>
                        <div className={`text-sm font-bold font-mono shrink-0 ${isEleitoral ? 'text-amber-300' : 'text-emerald-400'}`}>
                          {brl(y.total)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Tipo de emenda */}
            <Section title="Por tipo de emenda" testid="section-tipos">
              <div className="grid sm:grid-cols-2 gap-2">
                {(data.por_tipo_emenda || []).map((t, i) => (
                  <div
                    key={t.tipo + i}
                    className="px-3 py-2 rounded bg-neutral-900 border border-neutral-800"
                    data-testid={`tipo-${i}`}
                  >
                    <div className="text-xs text-neutral-400">{t.tipo}</div>
                    <div className="text-sm font-bold text-emerald-400 font-mono">
                      {brl(t.total)}
                    </div>
                    <div className="text-[10px] text-neutral-500">
                      {num(t.count)} pagamentos
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Categoria de despesa (dado oficial do CSV) */}
            {(data.por_categoria || []).length > 0 && (
              <Section
                title="Por categoria de despesa"
                subtitle="Capital = obras e equipamentos duráveis. Corrente = custeio, salários, materiais de consumo."
                testid="section-categoria"
              >
                <div className="grid sm:grid-cols-2 gap-2">
                  {(data.por_categoria || []).map((c, i) => (
                    <div
                      key={c.categoria + i}
                      className="px-3 py-2 rounded bg-neutral-900 border border-neutral-800"
                      data-testid={`categoria-${i}`}
                    >
                      <div className="text-xs text-neutral-400">{c.categoria}</div>
                      <div className="text-sm font-bold text-emerald-400 font-mono">
                        {brl(c.total)}
                      </div>
                      <div className="text-[10px] text-neutral-500">
                        {num(c.count)} pagamentos · {((c.total / data.total) * 100).toFixed(1)}% do total
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Top favorecidos */}
            <FavorecidosList favorecidos={data.top_favorecidos || []} />

            <div className="text-[11px] text-neutral-500 pt-2 flex items-center gap-1.5">
              <ExternalLink size={12} /> Fonte: {data.fonte}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const cnpjFmt = (c) => {
  const s = (c || '').replace(/\D/g, '');
  if (s.length !== 14) return c || 'sem CNPJ';
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12, 14)}`;
};

function FavorecidosList({ favorecidos }) {
  const PAGE = 50;
  const [page, setPage] = React.useState(1);
  const total = favorecidos.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE));
  const start = (page - 1) * PAGE;
  const items = favorecidos.slice(start, start + PAGE);

  return (
    <Section
      title="Quem recebeu (empresas e instituições)"
      subtitle={`Total: ${num(total)} beneficiários únicos, ordenados por valor recebido.`}
      testid="section-top-favs"
    >
      <div className="space-y-2">
        {items.map((f, i) => {
          const hasCnpj = f.cnpj && String(f.cnpj).replace(/\D/g, '').length === 14;
          const inner = (
            <Card className="bg-neutral-900 border-neutral-800 p-3 hover:border-emerald-500 transition flex items-center gap-3">
              <div className="text-xs font-mono text-neutral-500 w-6 shrink-0">
                {String(start + i + 1).padStart(2, '0')}
              </div>
              <Building2 size={16} className="text-neutral-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white break-words">{f.nome || 'Sem nome'}</div>
                <div className="text-[10px] text-neutral-500 font-mono mt-0.5">
                  {hasCnpj ? cnpjFmt(f.cnpj) : 'sem CNPJ'} · {f.area_label}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-emerald-400 font-mono">
                  {brl(f.total)}
                </div>
                <div className="text-[10px] text-neutral-500">{num(f.count)} pgto.</div>
              </div>
              {hasCnpj && <ChevronRight size={14} className="text-neutral-500 shrink-0" />}
            </Card>
          );
          return hasCnpj ? (
            <Link
              key={(f.cnpj || '') + (start + i)}
              to={`/brasil-transparente/empresa/${f.cnpj}`}
              data-testid={`fav-${start + i}`}
              className="block"
            >
              {inner}
            </Link>
          ) : (
            <div key={(f.nome || '') + (start + i)} data-testid={`fav-${start + i}`} className="cursor-default">
              {inner}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between gap-2" data-testid="favs-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            data-testid="page-prev"
            className="px-3 py-1.5 rounded bg-neutral-900 border border-neutral-800 text-xs text-neutral-300 disabled:opacity-40 hover:border-emerald-500 disabled:hover:border-neutral-800"
          >← Anterior</button>
          <div className="text-xs text-neutral-500">
            Página {page} de {totalPages}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            data-testid="page-next"
            className="px-3 py-1.5 rounded bg-neutral-900 border border-neutral-800 text-xs text-neutral-300 disabled:opacity-40 hover:border-emerald-500 disabled:hover:border-neutral-800"
          >Próxima →</button>
        </div>
      )}
    </Section>
  );
}

/* ── UI helpers ─────────────────────────────────────────── */
function Metric({ label, value, hint, testid }) {
  return (
    <Card className="bg-neutral-900 border-neutral-800 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{label}</div>
      <div className="text-lg sm:text-xl font-black text-white mt-1 leading-tight break-words">
        {value}
      </div>
      {hint && <div className="text-[10px] text-neutral-500 mt-1">{hint}</div>}
    </Card>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="text-sm font-semibold text-neutral-200">{title}</h2>
      {subtitle && (
        <p className="text-[11px] text-neutral-500 mb-2 mt-0.5">{subtitle}</p>
      )}
      <div className="mt-2">{children}</div>
    </section>
  );
}

function Bar({ label, value, max, hint, testid }) {
  const pct = max > 0 ? Math.max(3, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="relative rounded overflow-hidden bg-neutral-900 border border-neutral-800" data-testid={testid}>
      <div
        className="absolute inset-y-0 left-0 bg-emerald-500/20"
        style={{ width: `${pct}%` }}
      />
      <div className="relative flex items-center gap-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white truncate">{label}</div>
          {hint && <div className="text-[10px] text-neutral-500">{hint}</div>}
        </div>
        <div className="text-sm font-bold text-emerald-400 font-mono shrink-0">
          {brl(value)}
        </div>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="bt-muni-skeleton">
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
    </div>
  );
}

function ErrorBox() {
  return (
    <Card className="bg-red-950/30 border-red-900 p-4 text-sm text-red-200 flex gap-2" data-testid="bt-muni-error">
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <div>Não foi possível carregar os dados deste município.</div>
    </Card>
  );
}
